import { Progress } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNotification } from "../../hooks/useNotification";
import { useGetDataResetCurrentQuery } from "../../store/apiSlice/accountApiSlice";
import { addDiscoveredTask } from "../../store/slice/notificationSlice";
import {
  DATA_CLEAR_START_GRACE_MS,
  DATA_CLEAR_STORAGE_KEY,
  DATA_CLEAR_TASK_TITLE,
  DATA_CLEAR_TASK_TYPE,
  clearDataClearPendingReload,
  consumeDataClearReloadToast,
  dataClearMarkedAtMs,
  dataClearStorageKey,
  formatClearEta,
  hasDataClearNotified,
  hasDataClearPendingReload,
  isClearMarkedInStorage,
  isDataClearTask,
  markDataClearNotified,
  markDataClearPendingReload,
  markDataClearReloadToast,
  persistDataClearIdle,
  persistDataClearRunning,
  pushClearProgressSample,
  releaseStaleDataClearMark,
  scheduleDataClearWindowReload,
  setDataClearSuppressesTaskManager,
} from "./dataClearConstants";
import {
  StyledClearLock,
  StyledClearLockCard,
  StyledClearLockEta,
  StyledClearLockProgress,
  StyledClearLockStep,
} from "./index.styles";

/** @deprecated Use DATA_CLEAR_START_GRACE_MS */
const START_GRACE_MS = DATA_CLEAR_START_GRACE_MS;

/**
 * Company id from the signed-in user record.
 *
 * @param {Object|null|undefined} user
 * @return {string|number|undefined}
 */
const companyIdOf = user => user?.company_id ?? user?.company?.id;

/**
 * @param {string|number|undefined|null} companyId
 * @return {number} Epoch ms from storage value, or 0.
 */
const markedAtMs = companyId => dataClearMarkedAtMs(companyId);

/**
 * Remember a running clear in this tab and broadcast to other same-browser tabs.
 *
 * @param {string|number|undefined|null} companyId
 */
export const markDataClearRunning = companyId => {
  markDataClearPendingReload(companyId);
  if (persistDataClearRunning(companyId)) {
    window.dispatchEvent(new Event("data-clear-changed"));
  }
};

/**
 * Forget the remembered clear once the server says it is finished.
 *
 * @param {string|number|undefined|null} companyId
 */
export const clearDataClearMark = companyId => {
  persistDataClearIdle(companyId);
  window.dispatchEvent(new Event("data-clear-changed"));
};

/** @deprecated Use markDataClearRunning */
export const markInboundClearRunning = markDataClearRunning;

/** @deprecated Use clearDataClearMark */
export const clearInboundClearMark = clearDataClearMark;

export const INBOUND_CLEAR_STORAGE_KEY = DATA_CLEAR_STORAGE_KEY;

/**
 * @param {object|null|undefined} current
 * @param {object} tasksMap
 * @return {string}
 */
const resolveTaskId = (current, tasksMap) => {
  if (current?.task_id) return current.task_id;
  const discovered = Object.values(tasksMap || {}).find(isDataClearTask);
  return discovered?.task_id || "";
};

/**
 * Keep the app locked for every company user/window while a clear runs.
 *
 * Mounted once from TaskProvider so every route/tab gets the overlay.
 *
 * @return {JSX.Element|null}
 */
export const DataClearLock = () => {
  const dispatch = useDispatch();
  const notify = useNotification();
  const user = useSelector(state => state.auth.user);
  const companyId = companyIdOf(user);
  const tasksMap = useSelector(state => state.notification?.tasks);
  const [remembered, setRemembered] = useState(() => isClearMarkedInStorage(companyId));
  const [etaTick, setEtaTick] = useState(0);
  const [graceTick, setGraceTick] = useState(0);
  const finishHandledRef = useRef(false);
  const sawRunningRef = useRef(false);
  const progressSamplesRef = useRef([]);
  const reloadScheduledRef = useRef(false);

  useEffect(() => {
    const sync = () => setRemembered(isClearMarkedInStorage(companyId));
    window.addEventListener("data-clear-changed", sync);
    window.addEventListener("inbound-clear-changed", sync);
    return () => {
      window.removeEventListener("data-clear-changed", sync);
      window.removeEventListener("inbound-clear-changed", sync);
    };
  }, [companyId]);

  // Success toast survives the hard reload after a clear finishes.
  useEffect(() => {
    if (!companyId || !consumeDataClearReloadToast(companyId)) return;
    notify({
      type: "success",
      message: "Records cleared. Master data was not removed.",
    });
  }, [companyId, notify]);

  // Fetch less: one GET on mount/focus/reconnect. No idle/active poll.
  const {
    data: current,
    refetch,
    isSuccess: currentLoaded,
  } = useGetDataResetCurrentQuery(undefined, {
    skip: !user,
    pollingInterval: 0,
  });

  useEffect(() => {
    const storageKey = dataClearStorageKey(companyId);
    const onStorage = event => {
      if (event.key !== storageKey && event.key !== DATA_CLEAR_STORAGE_KEY) return;
      if (event.newValue && String(event.newValue).startsWith("running")) {
        persistDataClearRunning(companyId);
        markDataClearPendingReload(companyId);
        setRemembered(true);
        refetch();
      } else if (hasDataClearPendingReload(companyId)) {
        // Sibling tab finished — reload this tab too.
        setRemembered(false);
        if (!reloadScheduledRef.current) {
          reloadScheduledRef.current = true;
          markDataClearReloadToast(companyId);
          clearDataClearPendingReload(companyId);
          scheduleDataClearWindowReload();
        }
      } else {
        persistDataClearIdle(companyId);
        setRemembered(false);
        refetch();
      }
      window.dispatchEvent(new Event("data-clear-changed"));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [companyId, refetch]);

  const jobStatus = current?.status;
  const jobRunning = jobStatus === "running";
  const jobFinished = jobStatus === "succeeded" || jobStatus === "failed";
  const withinStartGrace = Date.now() - markedAtMs(companyId) < START_GRACE_MS;
  // graceTick forces a re-render when Confirm→POST grace ends.
  void graceTick;

  useEffect(() => {
    if (!remembered || jobRunning) return undefined;
    const markedAt = markedAtMs(companyId);
    if (!markedAt) return undefined;
    const remaining = START_GRACE_MS - (Date.now() - markedAt);
    if (remaining <= 0) {
      if (releaseStaleDataClearMark(companyId, jobStatus ?? null)) {
        setRemembered(false);
      }
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setGraceTick(value => value + 1);
      if (releaseStaleDataClearMark(companyId, jobStatus ?? null)) {
        setRemembered(false);
      }
    }, remaining + 50);
    return () => window.clearTimeout(timer);
  }, [companyId, jobRunning, jobStatus, remembered]);

  // Keep lock through Confirm→POST while cache may still show the previous succeeded job.
  const locked =
    jobRunning || (remembered && (!jobFinished || withinStartGrace || sawRunningRef.current));

  const taskId = jobRunning ? resolveTaskId(current, tasksMap) : "";

  useEffect(() => {
    if (!taskId) return;
    dispatch(
      addDiscoveredTask({
        task_id: taskId,
        is_visible: false,
        data: { title: DATA_CLEAR_TASK_TITLE, type: DATA_CLEAR_TASK_TYPE },
        title: DATA_CLEAR_TASK_TITLE,
      }),
    );
  }, [dispatch, taskId]);

  // Previous succeeded job (progress 100) stays in cache through Confirm→POST.
  const awaitingLiveJob =
    locked && !jobRunning && !sawRunningRef.current && (withinStartGrace || remembered);
  const percent = awaitingLiveJob ? 0 : Math.min(100, Number(current?.progress) || 0);
  const step = awaitingLiveJob ? "Clearing…" : current?.step || (locked ? "Clearing…" : "");
  const startedAt = awaitingLiveJob ? undefined : current?.started_at;
  const failed = jobStatus === "failed" && !awaitingLiveJob;

  useEffect(() => {
    if (!locked || awaitingLiveJob) {
      progressSamplesRef.current = [];
      return undefined;
    }
    progressSamplesRef.current = pushClearProgressSample(progressSamplesRef.current, percent);
    return undefined;
  }, [awaitingLiveJob, locked, percent]);

  useEffect(() => {
    if (!locked || failed) return undefined;
    const id = window.setInterval(() => setEtaTick(value => value + 1), 5000);
    return () => window.clearInterval(id);
  }, [failed, locked]);

  const eta = useMemo(
    () => formatClearEta(percent, startedAt, progressSamplesRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [etaTick, percent, startedAt, step],
  );

  useEffect(() => {
    if (jobStatus === "running") {
      sawRunningRef.current = true;
      finishHandledRef.current = false;
      markDataClearPendingReload(companyId, current?.id);
      markDataClearRunning(companyId);
      setDataClearSuppressesTaskManager(true);
      if (!remembered) setRemembered(true);
      return;
    }
    if (!currentLoaded) return;

    // During Confirm→POST, cache still shows the previous finished job. Keep the
    // mark — do not treat that stale succeeded/failed as "this clear ended".
    if (withinStartGrace && !sawRunningRef.current && !hasDataClearPendingReload(companyId)) {
      return;
    }

    if (
      jobFinished &&
      !finishHandledRef.current &&
      (sawRunningRef.current || hasDataClearPendingReload(companyId) || !withinStartGrace)
    ) {
      const jobId = current?.id;
      const pendingReload = hasDataClearPendingReload(companyId);
      const watchedThisClear = sawRunningRef.current || pendingReload;

      finishHandledRef.current = true;
      clearDataClearMark(companyId);
      setRemembered(false);
      sawRunningRef.current = false;

      if (jobStatus === "succeeded" && watchedThisClear) {
        if (!hasDataClearNotified(companyId, jobId)) {
          markDataClearNotified(companyId, jobId);
          markDataClearReloadToast(companyId);
        }
        if (!reloadScheduledRef.current) {
          reloadScheduledRef.current = true;
          clearDataClearPendingReload(companyId);
          scheduleDataClearWindowReload();
        }
        return;
      }

      if (jobStatus === "failed" && watchedThisClear) {
        clearDataClearPendingReload(companyId);
        if (!hasDataClearNotified(companyId, jobId)) {
          markDataClearNotified(companyId, jobId);
          notify({
            type: "error",
            message: current?.message || current?.step || "Clearing could not finish. Try again.",
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    current?.id,
    current?.message,
    currentLoaded,
    jobFinished,
    jobStatus,
    notify,
    remembered,
    withinStartGrace,
  ]);

  useEffect(() => {
    const appRoot = document.getElementById("root");
    if (!locked || !appRoot) return undefined;
    appRoot.setAttribute("inert", "");
    return () => appRoot.removeAttribute("inert");
  }, [locked]);

  useEffect(() => {
    if (!user) return undefined;
    const onFocus = () => refetch();
    const onVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refetch, user]);

  if (!user || !locked) return null;

  return createPortal(
    <StyledClearLock role="alertdialog" aria-modal="true" aria-labelledby="clear-lock-title">
      <StyledClearLockCard>
        <h3 id="clear-lock-title">Clearing records</h3>
        <StyledClearLockStep>{failed ? current?.message || step : step}</StyledClearLockStep>
        <StyledClearLockProgress>
          <Progress
            percent={Math.min(100, Number(percent) || 0)}
            status={failed ? "exception" : percent >= 100 ? "success" : "active"}
            strokeColor="#cf324a"
          />
        </StyledClearLockProgress>
        {!failed && <StyledClearLockEta>{eta}</StyledClearLockEta>}
        <p>You can leave this page. The company stays locked until clearing finishes.</p>
      </StyledClearLockCard>
    </StyledClearLock>,
    document.body,
  );
};

/** @deprecated Use DataClearLock */
export const InboundClearLock = DataClearLock;
